
import { Component, Prop, Vue } from "vue-property-decorator";
import PModal from "@/components/look/PModal.vue";
import { VueComponent } from "@/vue-ts-component";

import { Action, Getter } from "vuex-class";
import {
  ISolCustomerGetAssistanceServiceResponse,
  ISolCustomerGetDeliveryServiceResponse,
  ISolCustomerGetPickUpServiceResponse,
  ISolCustomerGetServiceParams,
  ISolCustomerGetSupplyMaterialServiceResponse,
} from "@/services";
import { BModal } from "bootstrap-vue";
import {
  IAssistanceService,
  ICountryConfig,
  IDeliveryService,
  IOrderConfigCommon,
  IPickUpService,
  IRequestHistorialsService,
  resolveSolCustomerOrderDescriptionKey,
  resolveSolCustomerOrderType,
  ISolCustomerCancellationReason,
  SolCustomerOrderType,
  ISolCustomerOrderService,
  ISolCustomerPack,
  ISolCustomerSupplyMaterialService,
  IUserDetailData,
  ICountryConfigResponse,
  IUserClientSubclientServicepointData,
} from "@/entities";
import VueFilters from "@/vue-filters";
import { mapGetters } from "vuex";
import DetailPickup from "@/components/orders/DetailPickup.vue";
import DetailDelivery from "@/components/orders/DetailDelivery.vue";
import DetailAssistance from "@/components/orders/DetailAssistance.vue";
import DetailSupply from "@/components/orders/DetailSupply.vue";
import DetailConsignment from "@/components/orders/DetailConsignment.vue";

@Component({
  name: "detail-order-modal",
  mixins: [VueFilters],
  computed: {
    ...mapGetters(["getLocale"]),
  },
  components: {
    DetailConsignment,
    PModal,
    DetailPickup,
    DetailDelivery,
    DetailAssistance,
    DetailSupply,
  },
})
export default class DetailOrderModal extends Vue {
  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData;
  @Getter("getCountryConfig") countryConfig!: ICountryConfig | undefined;
  @Getter("getCashCountryConfig") cashCountryConfig!: ICountryConfigResponse | undefined;
  @Action("fetchPickUpService") fetchPickUpService!: (
    params: ISolCustomerGetServiceParams
  ) => Promise<ISolCustomerGetPickUpServiceResponse | undefined>;
  @Action("fetchAssistanceService") fetchAssistanceService!: (
    params: ISolCustomerGetServiceParams
  ) => Promise<ISolCustomerGetAssistanceServiceResponse | undefined>;
  @Action("fetchDeliveryService") fetchDeliveryService!: (
    params: ISolCustomerGetServiceParams
  ) => Promise<ISolCustomerGetDeliveryServiceResponse | undefined>;
  @Action("fetchSupplyMaterialService") fetchSupplyMaterialService!: (
    params: ISolCustomerGetServiceParams
  ) => Promise<ISolCustomerGetSupplyMaterialServiceResponse | undefined>;
  @Prop({ required: true })
  entityId!: string | undefined;
  @Prop({ required: true })
  cancellationReasons!: ISolCustomerCancellationReason[];
  @Prop({ required: false, default: false })
  agilePickup!: boolean;
  @Prop({ required: true })
  packs!: ISolCustomerPack[];
  @Prop({ required: false, default: false }) isOrdersTable!: boolean;
  @Prop({ required: false, default: null })
  servicepointSelected!: IUserClientSubclientServicepointData | null;

  serviceStatusCode = "";
  serviceId: string | undefined;
  serviceCode: string | undefined = "";
  serviceType: number | undefined | null = null;
  serviceTypeDesc: string | undefined = "";
  creationDate: string | null = null;
  service: ISolCustomerOrderService | undefined = {};
  pickUpService: IPickUpService | undefined = {};
  assistanceService: IAssistanceService | undefined = {};
  deliveryService: IDeliveryService | undefined = {};
  supplyMaterialService: ISolCustomerSupplyMaterialService | undefined = {};

  async mounted(): Promise<void> {
    this.$on("showDetailOrderModal", (data: IRequestHistorialsService) => {
      this.showModal(data);
    });
  }

  async beforeDestroy(): Promise<void> {
    this.$off("showDetailOrderModal");
  }

  async showModal(data: IRequestHistorialsService): Promise<void> {
    const countryCode = this.getLoggedUser.country?.code ?? "";
    this.serviceType = data.serviceType;
    this.serviceId = data.serviceId;
    this.serviceCode = data.serviceCode;
    this.creationDate = data.requestedTime || null;
    this.serviceStatusCode = data.serviceStatusCode || "";
    this.service = data;
    const params = {
      countryCode: countryCode,
      serviceId: this.serviceId,
    } as ISolCustomerGetServiceParams;

    const orderType = resolveSolCustomerOrderType(data.productCode, this.serviceType ?? undefined);
    this.serviceTypeDesc = this.resolveServiceTypeDescription(data, orderType);
    await this.assignServiceByOrderType(data, params, orderType);

    this.$set(
      this.service!,
      "motiveDescription",
      this.cancellationReasons?.find((element) => element.code === this.service?.motiveCode)
        ?.description || this.service?.motiveCode
    );

    const pModal = this.$refs.orderDetailModal as VueComponent<any>;
    const bModal = pModal.$children[0] as BModal;
    bModal.show();
  }

  resolveServiceTypeDescription(
    data: IRequestHistorialsService,
    orderType?: SolCustomerOrderType
  ): string {
    if (data.productCode && data.productDescription) {
      return this.$t(data.productDescription).toString();
    }

    const descriptionKey = resolveSolCustomerOrderDescriptionKey(orderType, data.isChange);
    return descriptionKey ? this.$t(descriptionKey).toString() : "";
  }

  async assignServiceByOrderType(
    data: IRequestHistorialsService,
    params: ISolCustomerGetServiceParams,
    orderType?: SolCustomerOrderType
  ): Promise<void> {
    switch (orderType) {
      case SolCustomerOrderType.PICKUP:
        this.pickUpService = data;
        if (!this.isOrdersTable) await this.getPickUpService(params);
        break;
      case SolCustomerOrderType.DELIVERY:
        this.deliveryService = data;
        if (!this.isOrdersTable) await this.getDeliveryService(params);
        break;
      case SolCustomerOrderType.ASSISTANCE:
        this.assistanceService = data;
        if (!this.isOrdersTable) await this.getAssistanceService(params);
        break;
      case SolCustomerOrderType.SUPPLY_MATERIAL:
        this.supplyMaterialService = data;
        if (!this.isOrdersTable) await this.getSupplyMaterialServiceService(params);
        break;
      default:
        break;
    }
  }

  async onRefreshDeclaredValues(): Promise<void> {
    const countryCode = this.getLoggedUser.country?.code ?? "";
    const params = {
      countryCode: countryCode,
      serviceId: this.serviceId,
    } as ISolCustomerGetServiceParams;

    await this.getPickUpService(params);
    this.$emit("success");
  }

  async getPickUpService(params: ISolCustomerGetServiceParams): Promise<void> {
    const res = await this.fetchPickUpService(params);
    this.pickUpService = res?.pickUpService;
    this.service = res?.pickUpService;
  }

  async getAssistanceService(params: ISolCustomerGetServiceParams): Promise<void> {
    const res = await this.fetchAssistanceService(params);
    this.assistanceService = res?.assistanceService;
    this.service = res?.assistanceService;
  }

  async getDeliveryService(params: ISolCustomerGetServiceParams): Promise<void> {
    const res = await this.fetchDeliveryService(params);
    this.deliveryService = res?.deliverService;
    this.service = res?.deliverService;
  }

  async getSupplyMaterialServiceService(params: ISolCustomerGetServiceParams): Promise<void> {
    const res = await this.fetchSupplyMaterialService(params);
    this.supplyMaterialService = res?.supplyMaterialService;
    this.service = res?.supplyMaterialService;
  }

  get pickupConfig(): IOrderConfigCommon {
    return this.countryConfig?.Orders.Pickup ?? {};
  }

  get deliveryConfig(): IOrderConfigCommon {
    return this.countryConfig?.Orders.Delivery ?? {};
  }
}
